import { useState } from "react"
import {
  Box,
  Button,
  Container,
  Flex,
  FormControl,
  Heading,
  Image,
  Input,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react"
import { motion } from "framer-motion"

import magic8 from "#/assets/magic-8ball.png"
import bgImage from "#/assets/newsletter-background.png"
import { isEmailValid } from "#/shared/utils"

type LandingNewsletterProps = {
  newsletterHeader?: string
  newsletterSubheader?: string
  newsletterLegal?: string
}

export default function LandingNewsletter({
  newsletterHeader = "",
  newsletterSubheader = "",
  newsletterLegal = "",
}: LandingNewsletterProps) {
  const [email, setEmail] = useState("")
  const [loading, setLoading] = useState(false)
  const toast = useToast()

  const handleSubmit = async () => {
    if (!email) {
      toast({
        title: "Email required",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      })
      return
    }

    if (!isEmailValid(email)) {
      toast({
        title: "Invalid email format",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      })
      return
    }

    setLoading(true)

    try {
      const response = await fetch(
        "https://api.hsforms.com/submissions/v3/integration/submit/46383448/784299a2-16e0-42a8-81fa-df22441b9cff",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            fields: [{ name: "email", value: email }],
          }),
        },
      )

      if (response.ok) {
        toast({
          title: "Thanks for subscribing!",
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top",
        })
        setEmail("")
      } else {
        throw new Error("Submission failed")
      }
    } catch (err) {
      toast({
        title: "Something went wrong",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Container
      w="100%"
      maxWidth="100%"
      bg="white"
      h="auto"
      mb="0px"
      background={`url(${bgImage})`}
      backgroundColor="#AEDFFF"
      backgroundPosition="center bottom"
      backgroundSize="cover"
    >
      <motion.div
        initial="hidden"
        transition={{ type: "spring", stiffness: 100, damping: 15 }}
        whileInView="visible"
        viewport={{ once: true, amount: 0.5 }}
        variants={{
          hidden: { y: 200, opacity: 0 },
          visible: { y: 0, opacity: 1 },
        }}
      >
        <Flex
          justifyContent="center"
          flexDirection={{ base: "column-reverse", md: "row" }}
          bg="transparent"
          maxW="1480px"
          margin="auto"
          gap="20px"
          p={{ base: "30px", md: "20px" }}
        >
          <Image
            src={magic8}
            width="100%"
            maxWidth={{ base: "100%", md: "550px", xl: "700px" }}
            height="100%"
            mb="80px"
            pt={{ base: "00px", md: "30px" }}
          />
          <Box
            textAlign="left"
            p={{ base: "20px 0", xl: "80px" }}
            maxWidth="900px"
          >
            <VStack spacing="6" w="100%" alignItems="flex-start">
              <Heading
                fontSize={{ base: "36px", md: "52px" }}
                as="h3"
                fontFamily="riforma.medium"
                fontWeight="500"
                lineHeight="120%"
                maxWidth={{ base: "100%", md: "100%" }}
                textAlign="left"
              >
                {newsletterHeader}
              </Heading>
              <Text
                fontSize={{ base: "18px", md: "23px" }}
                fontFamily="riforma.regular"
                fontWeight="500"
                lineHeight="110%"
                textAlign="left"
                mt={{ base: "0", md: "0px" }}
              >
                {newsletterSubheader}
              </Text>
              <Flex
                w="100%"
                flexDirection={{ base: "column", md: "row" }}
                gap="12px"
                mt="20px"
              >
                <FormControl id="email" isRequired flex="1">
                  <Input
                    type="email"
                    value={email}
                    border="0"
                    bg="white"
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email here..."
                    size="lg"
                    h="60px"
                  />
                </FormControl>
                <Button
                  onClick={handleSubmit}
                  isLoading={loading}
                  loadingText="Submitting"
                  bg="primary.black.100"
                  color="white"
                  size="lg"
                  h="60px"
                  fontSize={{ base: "18px", md: "20px" }}
                  borderRadius="12px"
                  className="newsletter-subscribe"
                  minW={{ base: "100%", md: "180px" }}
                  _hover={{
                    bg: "primary.red.100",
                    color: "primary.white.100",
                  }}
                >
                  Subscribe
                </Button>
              </Flex>

              <Text
                fontSize={{ base: "18px", md: "18px" }}
                fontFamily="riforma.regular"
                fontWeight="500"
                lineHeight="110%"
                textAlign="left"
                mt={{ base: "0", md: "20px" }}
                mb="40px"
                dangerouslySetInnerHTML={{ __html: newsletterLegal }}
              />
            </VStack>
          </Box>
        </Flex>
      </motion.div>
    </Container>
  )
}
